import { useObservable, useObservableState } from 'observable-hooks';
import { useFormContext } from 'react-hook-form';
import type { ApolloCache, FetchResult } from '@apollo/client';
import type { Observable } from 'rxjs';
import { combineLatest, map, of, switchMap } from 'rxjs';
import { useEffect } from 'react';
import { isEmpty, pick } from 'lodash-es';
import { switchHead } from '../../../utils/rxjs';
import { getClient } from '../../../utils/apollo-client';
import { ACTIVATE_SUBSCRIPTION_MUTATION } from './queries';
import type { ConfigHelper } from './configHelper';
import { confirm, ConfirmType } from '../../confirm/Confirm';
import warning from '../../../utils/logger';
import {
    addNotificationHack,
    NOTIFICATION_VARIANT,
    useNotifications,
} from '../../notifications/Notifications';

interface ActivateSubscriptionResult {
    activateSubscription: {
        success: boolean;
    };
}

async function activateSubscription(deviceName: string): Promise<void> {
    const mutationParameters = {
        variables: { deviceName },
        mutation: ACTIVATE_SUBSCRIPTION_MUTATION,
        update(
            cache: ApolloCache<ActivateSubscriptionResult>,
            { data }: FetchResult<ActivateSubscriptionResult>
        ) {
            if (!data?.activateSubscription.success) {
                return;
            }

            const swarmFeaturesRef = cache.identify({
                __typename: 'SwarmFeatures',
                deviceName,
            });

            // Set `SwarmFeatures.validSubscription` to true for the device we are
            // activating. This will also update any active queries listening to it.
            // In case a notification was shown as a result of this query, this
            // notification can be hidden automatically.
            cache.modify({
                id: swarmFeaturesRef,
                fields: {
                    validSubscription() {
                        return true;
                    },
                },
            });
        },
    };

    function addErrorNotification() {
        addNotificationHack({
            message: interpolate(gettext('SUBSCRIPTION_ACTIVATION_ERROR'), [deviceName]),
            variant: NOTIFICATION_VARIANT.ERROR,
            scrollToTop: true,
        });
    }
    function addSuccessNotification() {
        addNotificationHack({
            message: interpolate(gettext('SUBSCRIPTION_ACTIVATION_SUCCESS'), [deviceName]),
            variant: NOTIFICATION_VARIANT.SUCCESS,
            scrollToTop: true,
        });
    }

    return getClient()
        .mutate<ActivateSubscriptionResult>(mutationParameters)
        .then(({ data }) =>
            data?.activateSubscription.success ? addSuccessNotification() : addErrorNotification()
        )
        .catch((error: Error) => {
            addErrorNotification();
            warning({ message: error });
        });
}

function activateSubscriptionWithConfirm(deviceName: string): void {
    confirm({
        message: interpolate(gettext('ACTIVATE_SUBSCRIPTION'), [deviceName]),
        onConfirm: () => {
            void activateSubscription(deviceName);
        },
    });
}

interface InvalidSubscription {
    deviceName: string;
    canActivateSubscription: boolean;
}

interface ActivateSubscriptionProps {
    configHelper: ConfigHelper;
}

export function ActivateSubscription({ configHelper }: ActivateSubscriptionProps) {
    const { addNotification } = useNotifications();
    const { setValue } = useFormContext();

    const invalidSubscriptions$: Observable<InvalidSubscription[]> = useObservable(
        (inputs$) =>
            inputs$.pipe(
                switchHead(),
                switchMap((swarmFeaturesPerDevice) => {
                    if (isEmpty(swarmFeaturesPerDevice)) {
                        return of([]);
                    }

                    return of(
                        swarmFeaturesPerDevice
                            .filter((swarmFeatures) => !swarmFeatures.validSubscription)
                            .map((swarmFeatures) =>
                                pick(swarmFeatures, ['deviceName', 'canActivateSubscription'])
                            ) as InvalidSubscription[]
                    );
                })
            ),
        [configHelper.swarmFeatures$]
    );

    const canActivateCurrentSensor$: Observable<[boolean, string]> = useObservable(
        (inputs$) =>
            inputs$.pipe(
                switchMap((inputs) => combineLatest(inputs)),
                map(([invalidSubscriptions, deviceName]) => {
                    const found = invalidSubscriptions.find((e) => e.deviceName === deviceName);
                    const canActivate = found?.canActivateSubscription ?? false;
                    return [canActivate, deviceName] as [boolean, string];
                })
            ),
        [invalidSubscriptions$, configHelper.deviceName$]
    );

    // `invalidSubscriptions` is a list of devices that have no valid subscription.
    // This list will be turned into notifications in the effect below:
    // * In a measuring point form, this list will only contain the sensor
    //   filled into the deviceName field.
    // * In a project form, this list will be populated with the devices attached to
    //   the measuring points associated with the project.
    const invalidSubscriptions = useObservableState(invalidSubscriptions$, []);
    useEffect(() => {
        if (isEmpty(invalidSubscriptions)) {
            return undefined;
        }

        const notifications = invalidSubscriptions.map(({ deviceName, canActivateSubscription }) =>
            addNotification({
                message: (
                    <>
                        {interpolate(gettext('HAS_NO_SUBSCRIPTION'), [deviceName])}
                        {canActivateSubscription ? (
                            <>
                                {' '}
                                <a onClick={() => activateSubscriptionWithConfirm(deviceName)}>
                                    {gettext('CLICK_TO_ACTIVATE_SUBSCRIPTION')}
                                </a>
                            </>
                        ) : null}
                    </>
                ),
                variant: NOTIFICATION_VARIANT.ERROR,
            })
        );

        return () => {
            notifications.forEach((notification) => notification.remove());
        };
    }, [invalidSubscriptions, addNotification]);

    // `showActivateModal` controls a modal that is shown:
    // * on the measuring point form (detected by checking the output of deviceName$).
    // * when the device name has no subscription.
    // * and when the device has the ability to be activated.
    const [showActivateModal, deviceName] = useObservableState(canActivateCurrentSensor$, [
        false,
        '',
    ]);

    useEffect(() => {
        if (!showActivateModal) {
            return undefined;
        }

        // Keep track of all the confirmations we've opened. This allows us to close
        // the confirmation in these cases:
        // * When this React component is unmounted, for example, when navigating to
        //   a different page.
        // * Or when `showActivateModal` changes state. This can occur when the device
        //   name changes (via new API data is loaded into the form or a manual form input
        //   change), or when the result of the `configHelper.swarmFeatures$` changes.
        const removeConfirmFunctions: (() => void)[] = [];

        removeConfirmFunctions.push(
            confirm({
                message: gettext('SUBSCRIPTION_AUTO_ACTIVATE_WARNING'),
                onConfirm: () => {
                    void activateSubscription(deviceName);
                },
                onCancel: () => {
                    // Wrap with `setTimeout` to make sure the following code is executed at
                    // the end of the callstack, this makes sure that the previous modal is
                    // closed and we can safely open the new one.
                    setTimeout(() => {
                        removeConfirmFunctions.push(
                            confirm({
                                message: gettext('SUBSCRIPTION_AUTO_ACTIVATE_DECLINE_MESSAGE'),
                                type: ConfirmType.INFO,
                                // Empty out the device name in the form.
                                afterClose: () => setValue('deviceName', ''),
                            })
                        );
                    });
                },
            })
        );

        return () => {
            removeConfirmFunctions.forEach((removeConfirm) => removeConfirm());
        };
    }, [addNotification, showActivateModal, deviceName, setValue]);

    return null;
}
